import axios from 'axios';
import {
  ElMessage,
  ElMessageBox
} from 'element-plus';
import 'element-plus/es/components/message/style/css';
import {
  useAuthStore
} from '@/store/modules/auth';
import {
  appConfig
} from '@/config';
import {
  AppError,
  formatError,
  getErrorType,
  logError,
  showError
} from '@/utils/errorHandler';
import {
  ErrorType
} from '@/types/error';

// 当前正在进行的请求数量
let requestCount = 0;

// 新架构API的token管理
let newApiToken = null;
let newApiTokenExpire = null;

// 获取新架构API的token
const getNewApiToken = async () => {
  // 如果token存在且未过期，直接返回
  if (newApiToken && newApiTokenExpire && Date.now() < newApiTokenExpire) {
    return newApiToken;
  }

  try {
    const response = await axios.post(`${appConfig.newApi.tokenBaseURL}/token`, {
      username: 'dev',
      password: '8wUsRBRkV5u2QeXk'
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    newApiToken = response.data.data.token;
    // 设置token过期时间为1个月（考虑到网络延迟等因素，实际设置为29天）
    newApiTokenExpire = Date.now() + 29 * 24 * 60 * 60 * 1000;

    return newApiToken;
  } catch (error) {
    console.error('获取新架构API token失败:', error);
    throw error;
  }
};

// 显示加载状态
const showLoading = () => {
  if (requestCount === 0) {
    // 这里可以实现全局加载状态
    console.log('开始请求加载');
  }
  requestCount++;
};

// 隐藏加载状态
const hideLoading = () => {
  requestCount--;
  if (requestCount <= 0) {
    requestCount = 0;
    // 这里可以隐藏全局加载状态
    console.log('请求加载完成');
  }
};

// 请求重试逻辑
const handleRetry = (instance, config, error, retriesLeft) => {
  const originalRequest = config;

  if (retriesLeft <= 0) {
    return Promise.reject(error);
  }

  // 只对特定错误类型进行重试
  const shouldRetry = error.code === 'ECONNABORTED' ||
    (error.response &&
      (error.response.status === 408 ||
        error.response.status === 429 ||
        error.response.status >= 500));

  if (!shouldRetry) {
    return Promise.reject(error);
  }

  // 设置重试延迟
  const retryDelay = originalRequest.retryDelay || 1000;

  return new Promise(resolve => {
    setTimeout(() => {
      console.log(`请求重试 ${originalRequest.retry - retriesLeft + 1}/${originalRequest.retry}`);
      resolve(instance(originalRequest));
    }, retryDelay);
  });
};

// 创建axios实例并配置拦截器的工厂函数
const createAxiosInstance = (baseURL, timeout, defaultHeaders = {}, tokenHeaderKey = 'X-Auth-Token') => {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json;charset=utf-8',
      ...defaultHeaders
    }
  });

  // 请求拦截器
  instance.interceptors.request.use(
    async (config) => {
        // 设置默认重试配置
        config.retry = config.retry || appConfig.api.retry.default;
        config.retryDelay = config.retryDelay || appConfig.api.retryDelay;

        // 显示加载状态
        if (config.loading !== false) {
          showLoading();
        }

        // 添加token
        if (baseURL === appConfig.newApi.baseURL) {
          // 新架构API使用Bearer token
          const token = await getNewApiToken();
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // 旧架构API使用X-Auth-Token
          const store = useAuthStore();
          if (store.token) {
            config.headers = config.headers || {};
            config.headers[tokenHeaderKey] = store.token;
          }
        }

        // 添加请求时间戳，防止缓存
        if (config.method?.toUpperCase() === 'GET') {
          config.params = {
            ...config.params,
            _t: Date.now()
          };
        }

        return config;
      },
      (error) => {
        hideLoading();
        // 使用错误处理工具记录错误
        logError(error, 'Request Configuration Error');
        console.error('请求配置错误:', error);
        return Promise.reject(error);
      }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response) => {
      const config = response.config;
      hideLoading();

      const res = response.data;

      // 处理业务逻辑错误
      if (res.code && res.code !== 200) {
        const apiError = new AppError(
          res.msg || '请求失败',
          ErrorType.API,
          res.code,
          res
        );

        if (!config.skipErrorHandler) {
          // 记录错误
          logError(apiError, `API: ${config.url}`);

          // 针对特定业务错误码进行处理
          if ([401].includes(res.code)) {
            // 未授权，跳转到登录页
            ElMessage({
              message: res.msg || '',
              type: res.type === 'WARN' ? 'warning' : 'error',
            })
            const store = useAuthStore();
            store.logout();
          } else if ([602].includes(res.code)) {
            const msg = res.msg.replace(/\r\n/g, '<br>')
            ElMessageBox.alert(msg, '', {
              dangerouslyUseHTMLString: true,
              showClose: false,
              confirmButtonText: '确认',
              confirmButtonClass: 'w-full mt-5',
              customClass: '!pt-6 !px-8 !pb-8 !max-w-[500px]',
              callback: (action) => {
              },
            })
          } else {
            // 显示错误提示
            ElMessage({
              message: apiError.message,
              type: res.type === 'WARN' ? 'warning' : 'error',
            })
          }
        }

        return Promise.reject(apiError);
      }

      // 显示成功消息
      if (config.showSuccessMessage) {
        ElMessage.success(config.showSuccessMessage === true ? res.msg : config.showSuccessMessage);
      }

      return res;
    },
    async (error) => {
      const config = error.config;
      hideLoading();

      // 重试处理
      if (config && config.retry) {
        return handleRetry(instance, config, error, config.retry);
      }

      // 简单的错误处理 - 服务器错误、网络错误等
      if (!config?.skipErrorHandler) {
        // 获取错误类型和格式化错误信息
        const errorType = getErrorType(error);
        const errorMessage = formatError(error);

        // 创建自定义错误对象并记录日志
        const appError = new AppError(
          errorMessage,
          errorType,
          error.response?.status,
          error
        );
        logError(appError, `API: ${config?.url || 'unknown'}`);

        ElMessage.error(errorMessage);

        return Promise.reject(appError);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// 创建主服务实例
const service = createAxiosInstance(
  appConfig.api.baseURL,
  appConfig.api.timeout, {},
);

// 创建第三方API服务实例
const pService = createAxiosInstance(
  appConfig.pApi.baseURL,
  appConfig.pApi.timeout, {},
);

// 创建新架构API服务实例
const newService = createAxiosInstance(
  appConfig.newApi.baseURL,
  appConfig.newApi.timeout, {},
);

// 封装常用请求方法
const request = {
  get(url, params, config) {
    return service.get(url, {
      params,
      ...config
    });
  },

  post(url, data, config) {
    return service.post(url, data, config);
  },

  put(url, data, config) {
    return service.put(url, data, config);
  },

  delete(url, params, config) {
    return service.delete(url, {
      params,
      ...config
    });
  },

  // 上传文件
  upload(url, file, config) {
    const formData = new FormData();
    formData.append('file', file);
    return service.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    });
  },

  // 批量请求
  batch(requests) {
    return Promise.all(requests.map(req => req()));
  }
};

// 封装第三方API请求方法
const pRequest = {
  get(url, params, config) {
    return pService.get(url, {
      params,
      ...config
    });
  },

  post(url, data, config) {
    return pService.post(url, data, config);
  },

  put(url, data, config) {
    return pService.put(url, data, config);
  },

  delete(url, params, config) {
    return pService.delete(url, {
      params,
      ...config
    });
  }
};

// 封装新架构API请求方法（支持Bearer token）
const newRequest = {
  get(url, params, config) {
    return newService.get(url, {
      params,
      ...config
    });
  },

  post(url, data, config) {
    return newService.post(url, data, config);
  },

  put(url, data, config) {
    return newService.put(url, data, config);
  },

  delete(url, params, config) {
    return newService.delete(url, {
      params,
      ...config
    });
  },

  // 上传文件（新架构）
  upload(url, data, config) {
    return newService.post(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    });
  }
};

export default request;
export {
  service,
  pService,
  newService,
  request,
  pRequest,
  newRequest
};